import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, Heart, LogOut, LayoutDashboard, Shield, Search, Home as HomeIcon, User, BedDouble, KeyRound, Inbox } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useFavorites } from "@/context/FavoritesContext";

const links = [
  { to: "/properties?category=buy", label: "Buy" },
  { to: "/properties?category=rent", label: "Rent" },
  { to: "/properties?category=pg", label: "PG / Co-living" },
  { to: "/properties?category=commercial", label: "Commercial" },
];

export const Navbar = () => {
  const { user, logout } = useAuth();
  const { count } = useFavorites();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const close = (e) => {
      if (!e.target.closest?.("[data-nav-menu]")) setOpen(false);
    };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [open]);

  return (
    <header className="sticky top-0 z-50 bg-background/95 backdrop-blur-md border-b border-border">
      <div className="mx-auto max-w-[1400px] px-4 sm:px-5 md:px-10 h-[64px] sm:h-[76px] flex items-center gap-3 sm:gap-6">
        <Link to="/" className="flex items-center gap-2 text-primary shrink-0" data-testid="nav-logo">
          <HomeIcon className="h-6 w-6 sm:h-7 sm:w-7" strokeWidth={2.2} />
          <span className="font-display text-[17px] sm:text-xl tracking-tight">HOUSES7.COM</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8 mx-auto">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="text-sm font-semibold text-foreground/80 hover:text-foreground transition-colors duration-200"
              data-testid={`nav-link-${l.label.toLowerCase().split(" ")[0]}`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-1.5">
          <button
            onClick={() => navigate("/post-property")}
            className="hidden sm:block text-sm font-semibold px-4 py-2.5 rounded-full hover:bg-muted transition-colors duration-200"
            data-testid="nav-post-property-btn"
          >
            List your property
          </button>

          {user && (
            <button
              onClick={() => navigate("/shortlist")}
              className="relative p-2.5 rounded-full hover:bg-muted transition-colors duration-200"
              aria-label="Shortlist"
              data-testid="nav-shortlist-btn"
            >
              <Heart className="h-5 w-5" />
              {count > 0 && (
                <span className="absolute -right-0.5 -top-0.5 h-4 min-w-4 px-1 rounded-full bg-primary text-[10px] font-bold text-primary-foreground grid place-items-center">
                  {count}
                </span>
              )}
            </button>
          )}

          <div className="relative" data-nav-menu>
            <button
              onClick={() => setOpen(!open)}
              className="flex items-center gap-3 border border-border rounded-full pl-3.5 pr-1.5 py-1.5 hover:shadow-search transition-shadow duration-200"
              data-testid="nav-menu-btn"
            >
              <Menu className="h-4 w-4" />
              <span className="h-7 w-7 rounded-full bg-foreground text-background grid place-items-center text-[11px] font-bold">
                {user ? (user.name || user.email || "U").slice(0, 1).toUpperCase() : <User className="h-4 w-4" />}
              </span>
            </button>

            {open && (
              <div
                className="absolute right-0 z-[60] mt-2 w-60 rounded-xl border border-border bg-background py-2 shadow-panel"
                data-testid="nav-menu-dropdown"
                onClick={() => setOpen(false)}
              >
                {user ? (
                  <>
                    <p className="px-4 py-2 text-xs text-muted-foreground truncate">{user.email}</p>
                    <MenuItem icon={LayoutDashboard} label="Dashboard" testid="nav-dashboard-btn" onClick={() => navigate("/dashboard")} />
                    <MenuItem icon={BedDouble} label="PG/Stay management" testid="nav-stays-btn" onClick={() => navigate("/stays")} />
                    <MenuItem icon={KeyRound} label="Rentals" testid="nav-rentals-btn" onClick={() => navigate("/rentals")} />
                    <MenuItem icon={Heart} label="My shortlist" testid="nav-menu-shortlist" onClick={() => navigate("/shortlist")} />
                    <MenuItem icon={Inbox} label="Enquiries" testid="nav-enquiries-btn" onClick={() => navigate("/enquiries")} />
                    <MenuItem icon={Search} label="Browse properties" testid="nav-menu-browse" onClick={() => navigate("/properties")} />
                    {user.role === "admin" && (
                      <MenuItem icon={Shield} label="Admin" testid="nav-admin-btn" onClick={() => navigate("/admin")} />
                    )}
                    <div className="my-2 h-px bg-border" />
                    <MenuItem
                      icon={LogOut}
                      label="Log out"
                      testid="nav-logout-btn"
                      onClick={async () => {
                        await logout();
                        navigate("/");
                      }}
                    />
                  </>
                ) : (
                  <>
                    <MenuItem icon={User} label="Sign up" testid="nav-signup-btn" onClick={() => navigate("/signup")} />
                    <MenuItem icon={LogOut} label="Log in" testid="nav-login-btn" onClick={() => navigate("/login")} />
                    <div className="my-2 h-px bg-border" />
                    <MenuItem icon={BedDouble} label="PG/Stay management" testid="nav-stays-btn" onClick={() => navigate("/stays")} />
                    <MenuItem icon={KeyRound} label="Rentals" testid="nav-rentals-btn" onClick={() => navigate("/rentals")} />
                    <MenuItem icon={HomeIcon} label="List your property" testid="nav-menu-list" onClick={() => navigate("/post-property")} />
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

const MenuItem = ({ icon: Icon, label, onClick, testid }) => (
  <button
    onClick={onClick}
    className="flex w-full items-center gap-3 px-4 py-2.5 text-sm font-semibold hover:bg-muted transition-colors duration-150"
    data-testid={testid}
  >
    <Icon className="h-4 w-4 text-muted-foreground" /> {label}
  </button>
);

export default Navbar;
